import { useEffect, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { typeScan } from './asciiScanField';
import { describeCameraError, openRearCamera, releaseCamera, startBarcodeDecoding } from './barcodeCamera';
import { useMobileViewport } from './useMobileViewport';
import { CameraIcon } from './UltimateIcons';
import { ULTIMATE_MODAL } from './ultimateModal';

const stopBubbling = (event: SyntheticEvent) => event.stopPropagation();

interface CameraScanButtonProps {
  /** The scan box a read code is entered into. */
  input: () => HTMLInputElement | null;
  label?: string | undefined;
  disabled?: boolean | undefined;
  /** On a screen in the Admin Ultimate Design: the line icon instead of the emoji, and the dressed dialog. */
  ultimate?: boolean | undefined;
  /**
   * Whether a read ends with the Enter a scanner sends (the default). Off where Enter commits a write, so the
   * read fills the box and the person confirms it [SSD-7527].
   */
  enterAfterRead?: boolean | undefined;
}

/**
 * Opens the rear camera in a dialog and enters the first code it reads into the scan box as a scanner
 * would (see `typeScan`), then closes. The camera is released whenever the dialog closes or unmounts.
 */
export function CameraScanButton({
  input,
  label = 'Scan with camera',
  disabled = false,
  ultimate = false,
  enterAfterRead = true,
}: CameraScanButtonProps) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The camera runs while the dialog is open and its video exists; it stops the moment the dialog closes,
  // not when the fade-out ends, so a code read during the fade is never entered.
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const inputRef = useRef(input);
  inputRef.current = input;

  useEffect(() => {
    if (!video || !open) {
      return undefined;
    }
    let live = true;
    let stream: MediaStream | null = null;
    let stopDecoding: (() => void) | null = null;
    const start = async () => {
      try {
        stream = await openRearCamera();
        if (!live) {
          releaseCamera(stream);
          return;
        }
        const stop = await startBarcodeDecoding(stream, video, (text) => {
          // A read landing between Cancel and the stop below belongs to a dialog the operator already closed.
          if (!live) {
            return;
          }
          setOpen(false);
          const target = inputRef.current();
          if (target) {
            typeScan(target, text, { enter: enterAfterRead });
          }
        });
        if (live) {
          stopDecoding = stop;
        } else {
          stop();
        }
      } catch (err) {
        // A camera that opened but whose decoder failed to load is let go now, not when the dialog closes.
        releaseCamera(stream);
        if (live) {
          setError(describeCameraError(err));
        }
      }
    };
    void start();
    return () => {
      live = false;
      stopDecoding?.();
      releaseCamera(stream);
    };
  }, [video, open, enterAfterRead]);

  const close = () => setOpen(false);

  return (
    <>
      <Button
        type="button"
        variant="outline-secondary"
        aria-label={label}
        title={label}
        disabled={disabled}
        className="flex-shrink-0"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        {ultimate ? <CameraIcon /> : '📷'}
      </Button>
      {/* React events bubble out of a portal into the screen's tree; the dialog's clicks and keys are its own. */}
      <span className="d-none" onClick={stopBubbling} onKeyDown={stopBubbling}>
        <Modal
          show={open}
          onHide={close}
          centered
          fullscreen="sm-down"
          // The read code's box takes the cursor as the dialog fades, so the dialog must neither hold it nor hand it back.
          enforceFocus={false}
          restoreFocus={false}
          aria-label={label}
          {...(ultimate ? ULTIMATE_MODAL : {})}
        >
          <Modal.Header closeButton>
            <Modal.Title>Scan a barcode</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error ? (
              <Alert variant="danger" className="mb-0" role="alert">
                {error}
              </Alert>
            ) : (
              <>
                <video
                  ref={setVideo}
                  className="w-100 rounded bg-dark"
                  style={{ maxHeight: '60vh', objectFit: 'cover' }}
                  muted
                  playsInline
                  aria-label="Camera preview"
                />
                <div className="text-muted small mt-2">Point the camera at the barcode or QR code.</div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={close}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      </span>
    </>
  );
}

interface CameraScanFieldProps {
  children: ReactNode;
  label?: string | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
  ultimate?: boolean | undefined;
  /** See `CameraScanButton`: off where Enter would commit a write, so the person confirms the read. */
  enterAfterRead?: boolean | undefined;
}

/**
 * Puts a camera-scan button beside a scan box on mobile, where there is no hardware scanner to hand.
 * On desktop the box renders exactly as given, with nothing added around it.
 */
export function CameraScanField({
  children,
  label,
  disabled,
  className,
  ultimate,
  enterAfterRead,
}: CameraScanFieldProps) {
  const mobile = useMobileViewport();
  const boxRef = useRef<HTMLDivElement>(null);
  if (!mobile) {
    return <>{children}</>;
  }
  return (
    <div ref={boxRef} className={['d-flex gap-2 align-items-center', className].filter(Boolean).join(' ')}>
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        {children}
      </div>
      <CameraScanButton
        label={label}
        disabled={disabled}
        ultimate={ultimate}
        enterAfterRead={enterAfterRead}
        input={() => boxRef.current?.querySelector<HTMLInputElement>('input') ?? null}
      />
    </div>
  );
}
